<script>
    import { createEventDispatcher } from "svelte";
    import { Section, Button } from "../../lib/ui";
    import {
        CELL_CYCLE,
        ENEMY_KINDS,
        PATTERN_DIFFICULTIES,
        rowToCells,
        cellsToRow,
        emptyRow,
        newPattern,
    } from "./gameSlots.js";

    /** 저작된 편대 패턴. 비어 있으면 게임은 내장 기본 14종으로 논다. */
    export let patterns = [];
    /** 슬롯키 → 이미지 URL. 격자 칸에 저작자의 실제 적 그림을 띄우려고 받는다. */
    export let urls = {};

    const dispatch = createEventDispatcher();

    const kindByCell = Object.fromEntries(ENEMY_KINDS.map((k) => [k.cell, k]));

    const emit = (next) => dispatch("change", next);

    /** 칸을 누르면 빈칸→적1→적2→적3→빈칸 으로 돈다. 한 바퀴 돌면 지워지므로 지우기 버튼이 따로 없다. */
    const cycleCell = (pIdx, rIdx, cIdx) => {
        const next = patterns.map((p, i) => (i === pIdx ? { ...p, rows: [...p.rows] } : p));
        const cells = rowToCells(next[pIdx].rows[rIdx]);
        const at = CELL_CYCLE.indexOf(cells[cIdx]);
        cells[cIdx] = CELL_CYCLE[(at + 1) % CELL_CYCLE.length];
        next[pIdx].rows[rIdx] = cellsToRow(cells);
        emit(next);
    };

    const addRow = (pIdx) =>
        emit(patterns.map((p, i) => (i === pIdx ? { ...p, rows: [...p.rows, emptyRow()] } : p)));

    const duplicateRow = (pIdx, rIdx) => {
        emit(
            patterns.map((p, i) => {
                if (i !== pIdx) return p;
                const rows = [...p.rows];
                rows.splice(rIdx + 1, 0, rows[rIdx]);
                return { ...p, rows };
            }),
        );
    };

    const removeRow = (pIdx, rIdx) => {
        emit(
            patterns.map((p, i) => {
                if (i !== pIdx) return p;
                // 줄이 하나도 없는 패턴은 의미가 없다 — 마지막 한 줄은 비우기만 한다.
                if (p.rows.length <= 1) return { ...p, rows: [emptyRow()] };
                return { ...p, rows: p.rows.filter((_, r) => r !== rIdx) };
            }),
        );
    };

    const updateField = (pIdx, field, value) =>
        emit(patterns.map((p, i) => (i === pIdx ? { ...p, [field]: value } : p)));

    const addPattern = () => emit([...patterns, newPattern()]);

    const duplicatePattern = (pIdx) => {
        const src = patterns[pIdx];
        emit([
            ...patterns.slice(0, pIdx + 1),
            {
                ...newPattern(),
                name: src.name ? `${src.name} 사본` : "",
                difficulty: src.difficulty,
                rows: [...src.rows],
            },
            ...patterns.slice(pIdx + 1),
        ]);
    };

    const removePattern = (pIdx) => emit(patterns.filter((_, i) => i !== pIdx));

    /** 적이 한 마리도 없는 패턴은 나와도 아무 일이 없다 — 저작자에게 알려준다(런타임 isPlayable 과 같은 판정). */
    const isEmptyPattern = (p) => !p.rows.some((r) => /[123]/.test(r));

    const countByTier = (tier) => patterns.filter((p) => p.difficulty === tier).length;
</script>

<Section
    title="적 편대"
    description="적은 낱개로 떨어지지 않고 줄 단위로 모양을 유지한 채 함께 내려옵니다."
>
    <p class="tw-mb-2 tw-text-[13px] tw-leading-relaxed tw-text-muted-foreground">
        칸을 눌러 적을 놓으세요. 아래쪽 줄이 화면에 <strong class="tw-text-foreground">먼저</strong> 들어옵니다.
        한 줄을 가득 채우면 피할 틈이 없어져 반드시 쏴서 뚫어야 합니다.
    </p>
    <p class="tw-mb-4 tw-text-[13px] tw-leading-relaxed tw-text-muted-foreground">
        난이도는 시간이 지날수록 올라갑니다 — <strong class="tw-text-foreground">쉬움</strong>은 처음부터,
        <strong class="tw-text-foreground">보통</strong>은 중반부터,
        <strong class="tw-text-foreground">어려움</strong>은 후반부터 나옵니다.
    </p>
    <div class="tw-flex tw-flex-wrap tw-gap-4">
        {#each ENEMY_KINDS as k (k.cell)}
            <span class="tw-flex tw-items-center tw-gap-1.5 tw-text-xs tw-text-foreground">
                <span class="tw-h-2.5 tw-w-2.5 tw-rounded-[3px]" style="background:{k.color}"></span>
                {k.label}
                <em class="tw-not-italic tw-text-muted-foreground">{k.hint}</em>
            </span>
        {/each}
    </div>
</Section>

{#if patterns.length === 0}
    <Section>
        <div class="tw-rounded-lg tw-border tw-border-dashed tw-border-border tw-bg-muted/40 tw-p-5">
            <div class="tw-mb-1 tw-text-sm tw-font-bold tw-text-foreground">아직 만든 편대가 없어요</div>
            <div class="tw-text-[13px] tw-leading-relaxed tw-text-muted-foreground">
                지금은 기본으로 들어 있는 편대 14종으로 게임이 돌아갑니다. 하나라도 만들면 그때부터는
                <strong class="tw-text-foreground">여기 만든 것만</strong> 나옵니다.
            </div>
        </div>
    </Section>
{/if}

{#each patterns as p, pIdx (p.id)}
    <Section class={isEmptyPattern(p) ? "tw-border-amber-500/50" : ""}>
        <div class="tw-mb-3 tw-flex tw-flex-wrap tw-items-center tw-gap-2">
            <input
                class="tw-h-9 tw-min-w-0 tw-flex-1 tw-rounded-md tw-border tw-border-border tw-bg-background tw-px-3 tw-text-sm tw-text-foreground"
                type="text"
                placeholder="편대 이름 (예: 가운데 뚫린 벽)"
                value={p.name || ""}
                on:change={(e) => updateField(pIdx, "name", e.target.value)}
            />
            <select
                class="tw-h-9 tw-rounded-md tw-border tw-border-border tw-bg-background tw-px-2 tw-text-xs tw-text-foreground"
                value={p.difficulty}
                on:change={(e) => updateField(pIdx, "difficulty", e.target.value)}
            >
                {#each PATTERN_DIFFICULTIES as d (d.value)}
                    <option value={d.value}>{d.label} · {d.hint}</option>
                {/each}
            </select>
            <button
                type="button"
                class="tw-h-9 tw-cursor-pointer tw-rounded-md tw-border tw-border-border tw-bg-background tw-px-3 tw-text-xs tw-text-muted-foreground tw-transition-colors hover:tw-text-foreground"
                on:click={() => duplicatePattern(pIdx)}>복제</button
            >
            <button
                type="button"
                class="tw-h-9 tw-cursor-pointer tw-rounded-md tw-border tw-border-border tw-bg-background tw-px-3 tw-text-xs tw-text-muted-foreground tw-transition-colors hover:tw-border-destructive hover:tw-text-destructive"
                on:click={() => removePattern(pIdx)}>삭제</button
            >
        </div>

        {#if isEmptyPattern(p)}
            <div class="tw-mb-3 tw-text-xs tw-text-amber-600 dark:tw-text-amber-400">
                적이 하나도 없어서 이 편대는 나와도 아무 일이 없습니다.
            </div>
        {/if}

        <!-- 격자는 게임 화면처럼 어두운 바탕에 둔다 — 투명 PNG 적 그림이 밝은 바탕에선 안 보인다. -->
        <div class="pattern-canvas">
            {#each p.rows as row, rIdx}
                <div class="pattern-row tw-mb-1.5 tw-flex tw-items-center tw-gap-2.5">
                    <div class="tw-grid tw-grid-cols-5 tw-gap-1.5">
                        {#each rowToCells(row) as cell, cIdx}
                            <button
                                type="button"
                                class="pattern-cell {cell !== '.' ? 'is-filled' : ''}"
                                style={cell !== "." ? `border-color:${kindByCell[cell].color}` : ""}
                                on:click={() => cycleCell(pIdx, rIdx, cIdx)}
                                aria-label={cell === "." ? "빈칸" : kindByCell[cell].label}
                            >
                                {#if cell !== "."}
                                    {#if urls[kindByCell[cell].slot]}
                                        <img crossorigin="anonymous" class="tw-max-h-[84%] tw-max-w-[84%] tw-object-contain" src={urls[kindByCell[cell].slot]} alt="" />
                                    {:else}
                                        <span class="tw-h-[22px] tw-w-[22px] tw-rounded-md" style="background:{kindByCell[cell].color}"></span>
                                    {/if}
                                {/if}
                            </button>
                        {/each}
                    </div>
                    <div class="pattern-row-tools tw-flex tw-gap-1">
                        <button type="button" class="pattern-tool" on:click={() => duplicateRow(pIdx, rIdx)}>줄 복제</button>
                        <button type="button" class="pattern-tool is-danger" on:click={() => removeRow(pIdx, rIdx)}>줄 삭제</button>
                    </div>
                </div>
            {/each}
            <div class="tw-mt-1.5 tw-text-[11px] tw-text-white/45">↓ 먼저 들어옴</div>
        </div>

        <button
            type="button"
            class="tw-mt-3 tw-cursor-pointer tw-rounded-md tw-border tw-border-dashed tw-border-border tw-bg-transparent tw-px-3.5 tw-py-2 tw-text-xs tw-text-muted-foreground tw-transition-colors hover:tw-border-primary hover:tw-text-primary"
            on:click={() => addRow(pIdx)}>+ 줄 추가</button
        >
    </Section>
{/each}

<div>
    <Button variant="outline" on:click={addPattern}>+ 새 편대 만들기</Button>
</div>

<style>
    .pattern-canvas {
        padding: 12px;
        border-radius: 12px;
        background: #0d1430;
    }

    .pattern-cell {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 52px;
        height: 52px;
        padding: 0;
        border: 1px dashed rgba(255, 255, 255, 0.18);
        border-radius: 8px;
        background: rgba(255, 255, 255, 0.04);
        cursor: pointer;
        overflow: hidden;
    }

    .pattern-cell:hover {
        background: rgba(255, 255, 255, 0.12);
    }

    .pattern-cell.is-filled {
        border-style: solid;
        border-width: 2px;
        background: rgba(255, 255, 255, 0.08);
    }

    /* 줄 도구는 평소엔 숨기고 그 줄에 마우스를 올렸을 때만 보인다(격자를 시끄럽게 만들지 않으려고). */
    .pattern-row-tools {
        opacity: 0;
        transition: opacity 0.12s;
    }

    .pattern-row:hover .pattern-row-tools {
        opacity: 1;
    }

    .pattern-tool {
        padding: 5px 9px;
        border: 1px solid rgba(255, 255, 255, 0.2);
        border-radius: 8px;
        background: transparent;
        color: rgba(255, 255, 255, 0.6);
        font-size: 11px;
        font-family: inherit;
        cursor: pointer;
    }

    .pattern-tool:hover {
        color: #fff;
    }

    .pattern-tool.is-danger:hover {
        color: #ff8a8a;
        border-color: #ff8a8a;
    }
</style>
